import { useEffect } from 'react';
import { siteConfig } from '../config/site';

/**
 * Meta tags por página.
 *
 * Antes disto, o MetaTags corria uma única vez no App e declarava
 * rel="canonical" para a raiz em TODAS as páginas — o que diz ao Google que
 * cada página é uma cópia da homepage. Este componente dá a cada página o seu
 * próprio título, description, canonical e dados estruturados.
 *
 * Deve ser renderizado dentro da página, uma vez.
 */
interface PageMetaProps {
  /** Título do separador. Se não incluir o nome da empresa, é acrescentado. */
  title: string;
  description: string;
  /** Caminho canónico desta página, começado por "/". */
  path: string;
  keywords?: string;
  /** JSON-LD específico da página (Service, FAQPage, BreadcrumbList...). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Páginas que não devem ser indexadas (obrigado, 404). */
  noindex?: boolean;
}

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const PageMeta = ({ title, description, path, keywords, jsonLd, noindex }: PageMetaProps) => {
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const fullTitle = title.includes(siteConfig.name) ? title : `${title} | ${siteConfig.name}`;
    const canonical = `https://www.${siteConfig.domain}${path === '/' ? '/' : path}`;

    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    // robots: só escrevemos a tag quando a página pede noindex
    let robots: Element | null = null;
    if (noindex) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      robots.setAttribute('content', 'noindex, follow');
      document.head.appendChild(robots);
    }

    // JSON-LD da página, marcado para não colidir com o do site (MetaTags)
    let script: HTMLScriptElement | null = null;
    if (serializedJsonLd) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-meta', 'true');
      script.textContent = serializedJsonLd;
      document.head.appendChild(script);
    }

    return () => {
      robots?.remove();
      script?.remove();
    };
  }, [title, description, path, keywords, serializedJsonLd, noindex]);

  return null;
};

export default PageMeta;
